/**
 * @fileoverview Investigation snapshot card chrome
 * @module jobs/phases/overview/investigations/InvestigationSnapshotCard
 *
 * Purpose: Shared card shell for the compact "snapshot" of a single
 * investigation agent shown on the job Overview → Investigations tab. Each
 * snapshot renders a read-only summary of the agent's headline output plus a
 * "View detail" button that deep-links to the full Review & Analysis view.
 *
 * Used by: BooksRecordsSnapshot, SolvencySnapshot,
 * RecoverableTransactionsSnapshot, OffencesSnapshot.
 */

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { Button } from "@/ui/primitives/button";

/**
 * Matches the `GROUP_CARD_CLASS` used by the Overview section shells (see
 * plan.tsx) so the investigation snapshots sit flush with the other cards.
 */
const CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

interface InvestigationSnapshotCardProps {
  /** Lucide icon rendered beside the title (mirrors the review registry). */
  icon: LucideIcon;
  /** Human-readable agent name. */
  title: string;
  /** Deep-links to the agent's Review & Analysis detail view. */
  onSeeDetails: () => void;
  /** Snapshot body. */
  children: ReactNode;
}

/**
 * Standard chrome for an investigation snapshot: icon + title on the left, a
 * "View detail" link on the right, and the snapshot body below a divider.
 *
 * @component
 * @example
 * <InvestigationSnapshotCard icon={BookCheck} title="Books & Records" onSeeDetails={go}>
 *   {content}
 * </InvestigationSnapshotCard>
 */
export function InvestigationSnapshotCard({
  icon: Icon,
  title,
  onSeeDetails,
  children,
}: InvestigationSnapshotCardProps) {
  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h4 className="truncate text-sm font-semibold">{title}</h4>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="-mr-2 gap-1 text-xs text-muted-foreground"
          onClick={onSeeDetails}
        >
          View detail
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
      <div className="border-t border-border">{children}</div>
    </div>
  );
}
